import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlarmClock, AtSign, BellRing, Briefcase, CheckCheck, CheckCircle2, ChevronRight, ClipboardPen, Clock, ListChecks,
  MessageSquare, ReceiptText, Users, Wrench,
} from "lucide-react";
import OsobaKolecko from "./OsobaKolecko";
import ZapnoutUpozorneni from "./ZapnoutUpozorneni";
import { useTym } from "./KdoUvidi";
import { useUpozorneni } from "../lib/upozorneniKontext";
import { useOdznaky } from "../lib/mobilniNav";

/**
 * Zvonek: osobní upozornění (přidělený úkol, připomínka, po termínu, zmínka
 * a komentář u úkolu, přidání k případu a klientovi) svítí, dokud na ně člověk neklikne. Pod nimi souhrn,
 * co propadlo ve firmě (servis, nedostatky, rozpracované zápisy, doklady).
 * Telefon a tablet: spodní list (plátno 16A). Počítač: panel vedle levého menu.
 */
const PRECTENYCH = 6;

function kdy(d) {
  const t = new Date(d);
  const ted = new Date();
  const min = Math.round((ted - t) / 60000);
  if (min < 1) return "teď";
  if (min < 60) return `před ${min} min`;
  const dnes = new Date(ted.getFullYear(), ted.getMonth(), ted.getDate());
  if (t >= dnes) return `před ${Math.floor(min / 60)} h`;
  if (t >= new Date(dnes.getTime() - 86400000)) return "včera";
  return new Intl.DateTimeFormat("cs-CZ", { day: "numeric", month: "numeric" }).format(t);
}

const DLAZDICE = {
  PO_TERMINU: { ikona: AlarmClock, barva: "bg-[#fdeeec] text-danger" },
  PRIPOMINKA: { ikona: BellRing, barva: "bg-[#fff3e0] text-[#b45309]" },
  AKTUALNI: { ikona: Clock, barva: "bg-accent/15 text-[#a8410c]" },
  PRIDELENO: { ikona: ListChecks, barva: "bg-surface text-sec" },
  PRIDAN_K_PRIPADU: { ikona: Briefcase, barva: "bg-surface text-sec" },
  PRIDAN_K_KLIENTOVI: { ikona: Users, barva: "bg-surface text-sec" },
  ZMINKA: { ikona: AtSign, barva: "bg-accent/15 text-[#a8410c]" },
  KOMENTAR: { ikona: MessageSquare, barva: "bg-surface text-sec" },
};

/** Fotka toho, kdo upozornění vyvolal; u připomínek a „po termínu“ ikona. */
function Znak({ u, tym }) {
  const autor = u.autor_id && tym[u.autor_id];
  if (autor && !["PO_TERMINU", "PRIPOMINKA", "AKTUALNI"].includes(u.typ)) {
    return <OsobaKolecko id={autor.id} name={autor.name} url={autor.photo_url} size={36} />;
  }
  const d = DLAZDICE[u.typ] || DLAZDICE.PRIDELENO;
  const Ikona = d.ikona;
  return (
    <span className={`h-9 w-9 rounded-[11px] inline-flex items-center justify-center shrink-0 ${d.barva}`}>
      <Ikona size={17} strokeWidth={1.9} />
    </span>
  );
}

function Radek({ u, tym, prvni, onOtevri }) {
  return (
    <button type="button" onClick={() => onOtevri(u)} data-testid={`upozorneni-${u.id}`} data-precteno={u.precteno}
      className={`w-full min-h-[60px] px-3.5 py-2.5 flex items-start gap-3 text-left active:bg-inputbg lg:hover:bg-inputbg ${prvni ? "" : "border-t border-line-soft"}`}>
      <Znak u={u} tym={tym} />
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline gap-2">
          <span className={`flex-1 min-w-0 t-telo line-clamp-2 ${u.precteno ? "font-500 text-sec" : `font-700 ${u.typ === "PO_TERMINU" ? "text-danger" : "text-ink"}`}`}>
            {u.titulek}
          </span>
          <span className="t-meta text-muted shrink-0 whitespace-nowrap">{kdy(u.created_at)}</span>
        </span>
        {u.text && <span className="block t-meta text-muted line-clamp-2 mt-0.5">{u.text}</span>}
      </span>
      <span className={`mt-[7px] h-2 w-2 rounded-full shrink-0 ${u.precteno ? "bg-transparent" : "bg-accent"}`}
        aria-label={u.precteno ? undefined : "nepřečtené"} />
    </button>
  );
}

function Obsah({ odznaky, onClose }) {
  const nav = useNavigate();
  const { items, neprectenych, nacteno, precist, otevrit } = useUpozorneni();
  const lide = useTym();
  const tym = useMemo(() => Object.fromEntries(lide.map((m) => [m.id, m])), [lide]);
  const [starsi, setStarsi] = useState(false);
  const { souhrn, servis } = odznaky;

  const nove = items.filter((u) => !u.precteno);
  const prectene = items.filter((u) => u.precteno);
  const ukazat = starsi ? prectene : prectene.slice(0, PRECTENYCH);
  const jdi = (cesta) => { onClose(); nav(cesta); };
  const otevri = (u) => { onClose(); otevrit(u); };
  const souhrnne = [
    servis.po_terminu > 0 && { ikona: Wrench, titul: "Servis po termínu", pod: "zařízení čeká na prohlídku", pocet: servis.po_terminu, cesta: "/servis" },
    servis.nedostatky_po_terminu > 0 && { ikona: Wrench, titul: "Nedostatky po termínu", pod: "slíbené opravy klientům", pocet: servis.nedostatky_po_terminu, cesta: "/servis" },
    servis.rozpracovane > 0 && { ikona: ClipboardPen, titul: "Rozpracované zápisy", pod: "dopiš a nech podepsat", pocet: servis.rozpracovane, cesta: "/servis", seda: true },
    souhrn.pocet_dokladu_po_splatnosti > 0 && { ikona: ReceiptText, titul: "Doklady po splatnosti", pod: "neuhrazené faktury", pocet: souhrn.pocet_dokladu_po_splatnosti, cesta: "/doklady" },
  ].filter(Boolean);
  const nic = nacteno && !items.length && !souhrnne.length;

  return (
    <>
      <div className="px-1.5 pb-2 flex items-center gap-2 min-h-[36px]">
        <h2 className="t-sekce font-700 text-ink">Upozornění</h2>
        {neprectenych > 0 && <span className="amount t-meta text-muted font-600">{neprectenych}</span>}
        {neprectenych > 0 && (
          <button type="button" onClick={() => precist({})} data-testid="upozorneni-vse"
            className="tap lg:min-h-0 ml-auto -mr-1.5 px-1.5 inline-flex items-center gap-1.5 t-meta font-700 text-accent hover:text-accent-hover">
            <CheckCheck size={16} strokeWidth={2.2} /> Označit vše
          </button>
        )}
      </div>

      <ZapnoutUpozorneni />

      {nic && (
        <div className="bg-white rounded-[20px] px-4 py-6 flex flex-col items-center gap-2 text-center" data-testid="upozorneni-nic">
          <CheckCircle2 size={26} className="text-success" />
          <span className="t-telo font-600 text-ink">Všechno vyřízené</span>
          <span className="t-meta text-muted max-w-[300px]">Tady uvidíš úkoly, které ti někdo přidá, připomínky a co je po termínu.</span>
        </div>
      )}

      {nove.length > 0 && (
        <>
          <div className="label-up text-accent px-1.5 pt-1 pb-[7px]">Nové</div>
          <div className="bg-white rounded-[20px] overflow-hidden" data-testid="upozorneni-nove">
            {nove.map((u, j) => <Radek key={u.id} u={u} tym={tym} prvni={!j} onOtevri={otevri} />)}
          </div>
        </>
      )}

      {prectene.length > 0 && (
        <>
          <div className="label-up text-muted px-1.5 pt-3 pb-[7px]">Dříve</div>
          <div className="bg-white rounded-[20px] overflow-hidden" data-testid="upozorneni-drive">
            {ukazat.map((u, j) => <Radek key={u.id} u={u} tym={tym} prvni={!j} onOtevri={otevri} />)}
            {prectene.length > ukazat.length && (
              <button type="button" onClick={() => setStarsi(true)} data-testid="upozorneni-starsi"
                className="w-full h-12 border-t border-line-soft t-meta font-700 text-accent">
                Starší ({prectene.length - ukazat.length})
              </button>
            )}
          </div>
        </>
      )}

      {souhrnne.length > 0 && (
        <>
          <div className="label-up text-muted px-1.5 pt-3 pb-[7px]">Ve firmě</div>
          <div className="bg-white rounded-[20px] overflow-hidden" data-testid="upozorneni-souhrn">
            {souhrnne.map((r, j) => {
              const Ikona = r.ikona;
              return (
                <button key={r.titul} type="button" onClick={() => jdi(r.cesta)}
                  className={`w-full min-h-[56px] px-3.5 py-2 flex items-center gap-3 text-left active:bg-inputbg lg:hover:bg-inputbg ${j ? "border-t border-line-soft" : ""}`}>
                  <span className="h-9 w-9 rounded-[11px] bg-surface text-sec inline-flex items-center justify-center shrink-0"><Ikona size={17} strokeWidth={1.9} /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block t-telo font-600 text-ink truncate">{r.titul}</span>
                    <span className="block t-meta text-muted truncate">{r.pod}</span>
                  </span>
                  <span className={`amount min-w-[22px] h-[22px] px-1.5 rounded-full text-[11.5px] font-700 inline-flex items-center justify-center ${
                    r.seda ? "bg-surface text-sec" : "bg-danger text-white"}`}>{r.pocet}</span>
                  <ChevronRight size={16} className="text-[#c4c2bd] shrink-0" />
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

function useZavritEsc(onClose) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
}

/** Telefon a tablet: spodní list. */
export default function Upozorneni({ odznaky, onClose }) {
  useZavritEsc(onClose);
  return (
    <div className="lg:hidden fixed inset-0 z-50 flex items-end" data-testid="upozorneni" role="dialog" aria-modal="true" aria-label="Upozornění">
      <div className="absolute inset-0 bg-black/40 zaclon-in" onClick={onClose} />
      <div className="okno-in relative w-full max-h-[82vh] overflow-y-auto bg-surface rounded-t-sheet shadow-panel px-3.5 pt-2 pb-[calc(16px+env(safe-area-inset-bottom))] md:mx-auto md:max-w-[560px]">
        <span className="block w-10 h-[5px] rounded-full bg-line-strong mx-auto mb-3" />
        <Obsah odznaky={odznaky} onClose={onClose} />
      </div>
    </div>
  );
}

/** Počítač: panel vedle levého menu, zavře ho klik mimo nebo Esc. */
export function UpozorneniPanel({ onClose }) {
  const odznaky = useOdznaky();
  const ref = useRef(null);
  useZavritEsc(onClose);
  useEffect(() => {
    const mimo = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !e.target.closest?.('[data-testid="sidebar-zvonek"]')) onClose();
    };
    document.addEventListener("mousedown", mimo);
    return () => document.removeEventListener("mousedown", mimo);
  }, [onClose]);
  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()} data-testid="upozorneni-panel" role="dialog" aria-label="Upozornění"
      className="vyber-menu absolute z-40 left-full top-3 ml-5 w-[400px] max-h-[calc(100vh-24px)] overflow-y-auto bg-surface rounded-[20px] shadow-[0_18px_50px_rgba(20,22,26,.22)] border border-line-softer p-3">
      <Obsah odznaky={odznaky} onClose={onClose} />
    </div>
  );
}
